import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'
import TabActionButtons from '@/global/components/view-layouts/tabs-layout/components/TabActionButtons.vue'
import WorkOrderExecutionInfo from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/components/WorkOrderExecutionInfo.vue'
import { useWorkOrderExecutionFields } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderExecutionFields'
import { useWorkOrderMapData } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/useWorkOrderMapData'
import { applyExecutionForm } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/workOrderData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  WORK_ORDERS_ENDPOINT,
  WORK_ORDERS_OVERVIEW_INDEX_ROUTE,
  WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS,
  WORK_ORDER_EXECUTION_TAB_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

/**
 * The "Podaci o izvršenju" tab of a work order: how far the order actually got, beside
 * the route it was driving. Read-only, like the tab before it.
 *
 * It fetches the order itself rather than leaning on the task tab's copy: each tab of
 * the layout owns its own form data, and this one binds to different keys of the same
 * record.
 */
export function useWorkOrderExecution (options) {
  const { translate, workOrderId } = options || {}

  const { fields } = useWorkOrderExecutionFields({ translate })

  // What the map beside the form draws. Fetched here rather than by the block itself so
  // the tab knows when it is still coming: the layout's own loader covers the order's
  // fetch, and the route, the reports and the vehicle's position arrive after it - see
  // `onDataReceived`.
  const {
    streets,
    irregularities,
    vehicles,
    isLoading: isLoadingMapData,
    load: loadMapData
  } = useWorkOrderMapData()

  const apiConfig = freezeObject({
    get: {
      module: WINTER_SERVICE_MODULE,
      route: `${WORK_ORDERS_ENDPOINT}/${workOrderId}`
    }
  })

  const tabActions = computed(() => ({
    component: TabActionButtons,
    title: translate(`${NS}.work_order_details`),
    showEditButtons: false,
    showBackButton: {
      uiViewName: WORK_ORDERS_OVERVIEW_INDEX_ROUTE
    },
    additional_buttons: [
      {
        // Icon only, like the listing's own refresh button - the row it sits in is
        // already labelled by the title beside it.
        type: 'base',
        prependIcon: 'mdi-refresh',
        prependIconStyle: { marginRight: '0px' },
        // Re-fetching the order is the whole of it: the map is redrawn off the record
        // that comes back - see `onDataReceived`.
        click: ({ fetchTabData }) => fetchTabData?.()
      }
    ],
    visible: true
  }))

  const infoBlock = computed(() => ({
    component: WorkOrderExecutionInfo,
    visible: true,
    streets: streets.value,
    irregularities: irregularities.value,
    vehicles: vehicles.value,
    rows: [
      {
        type: 'fields',
        columnsCount: 1,
        fields: fields.value
      }
    ]
  }))

  /**
   * The order has arrived: the form is bound to it and the map is redrawn off it.
   *
   * Always `force`: this only runs on a fetch, and a fetch is either the tab opening or
   * the refresh button - neither of which wants what was drawn last time.
   */
  const onDataReceived = (apiData) => {
    const record = apiData?.data

    applyExecutionForm(translate, record)
    loadMapData(record ? [record] : [], { force: true })
  }

  return computed(() => ({
    key: WORK_ORDER_EXECUTION_TAB_KEY,
    title: translate(`${NS}.execution_data`),
    apiConfig,
    onDataReceived,
    // The layout drops its loader as soon as the order itself is in; the map is still
    // being fetched at that point, so the tab keeps it up until that is in too.
    showLoader: isLoadingMapData.value,
    tabContent: [
      tabActions.value,
      infoBlock.value
    ]
  }))
}
